import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { Sparkles, Star, PenLine, Palette, BookHeart, ShieldCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SAMPLES = [
  { id: "milo-moon-whale", title: "Milo & the Moon Whale", tag: "Bedtime", color: "#1D2A44", cover: "https://images.unsplash.com/photo-1600188769099-d25b4ec79659?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { id: "aria-forest-kindness", title: "Aria's Forest of Kindness", tag: "Learning", color: "#81B29A", cover: "https://images.unsplash.com/photo-1611150257812-e2296b43202b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { id: "kai-sky-dragon", title: "Kai & the Sky Dragon", tag: "Adventure", color: "#E07A5F", cover: "https://images.unsplash.com/photo-1543269865-cbf427effbad?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

const STEPS = [
  { id: "step-details", icon: PenLine, title: "Tell us about your kid", body: "Name, age, hair, skin tone, favorite toy." },
  { id: "step-theme", icon: Sparkles, title: "Pick a theme", body: "Dragons, bedtime, kindness — or your own idea." },
  { id: "step-style", icon: Palette, title: "Choose an art style", body: "Watercolor, cartoon, or classic storybook." },
  { id: "step-pdf", icon: BookHeart, title: "Get your PDF", body: "In about 90 seconds. Emailed + downloadable." },
];

export default function Landing() {
  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-6xl mx-auto px-5 pb-24">
        {/* HERO */}
        <section className="pt-10 lg:pt-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="pill-tag bg-[#F2CC8F]/60 text-[#5C3F1E] inline-flex items-center gap-1">
              <Sparkles size={13} /> Personalized in ~90 seconds
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mt-4 text-[#2B2D42]">
              A bedtime story <em className="text-[#E07A5F] not-italic">made</em> for your child.
            </h1>
            <p className="mt-5 text-lg text-[#4A4E69] max-w-xl">
              Their name, their look, their favorite thing — woven into an illustrated
              picture book you'll actually re-read. Ready in a minute. <strong>$8, one book.</strong>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/create" data-testid="hero-cta"
                className="pill-tag bg-[#E07A5F] hover:bg-[#D96B43] text-white text-base px-6 py-3 shadow-lg shadow-[#E07A5F]/30 flex items-center gap-2">
                <Sparkles size={16} /> Create your book — free preview
              </Link>
              <div className="flex items-center gap-1 text-sm text-[#5C6370]">
                <Star size={14} className="fill-[#F2CC8F] text-[#F2CC8F]" />
                <Star size={14} className="fill-[#F2CC8F] text-[#F2CC8F]" />
                <Star size={14} className="fill-[#F2CC8F] text-[#F2CC8F]" />
                <Star size={14} className="fill-[#F2CC8F] text-[#F2CC8F]" />
                <Star size={14} className="fill-[#F2CC8F] text-[#F2CC8F]" />
                <span className="ml-1">Loved by 1,200+ parents</span>
              </div>
            </div>
            <p className="mt-4 text-xs text-[#6C757D] flex items-center gap-1">
              <ShieldCheck size={13} /> Free preview before you pay — no surprises.
            </p>
          </div>

          {/* Book showcase */}
          <div className="lg:col-span-5 relative min-h-[420px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative w-[280px] sm:w-[320px] book-tilt floaty">
                <div className="paper-card overflow-hidden">
                  <img src={SAMPLES[0].cover} alt="cover"
                    className="w-full h-[380px] object-cover" />
                  <div className="p-4">
                    <div className="pill-tag bg-[#1D2A44] text-white inline-block">Bedtime</div>
                    <div className="font-display text-2xl font-bold mt-2 text-[#2B2D42]">Milo & the Moon Whale</div>
                    <div className="text-xs text-[#5C6370] mt-1">A personalized storybook for Milo, age 5</div>
                  </div>
                </div>
              </div>
              <span className="absolute top-4 right-4 twinkle text-[#F2CC8F] text-2xl">✦</span>
              <span className="absolute bottom-10 left-6 twinkle text-[#E07A5F] text-xl" style={{ animationDelay: ".8s" }}>✦</span>
            </div>
          </div>
        </section>

        {/* SAMPLES */}
        <section className="mt-24">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2B2D42]">Peek at real books.</h2>
            <span className="text-sm text-[#5C6370]">Every book is unique — these were made for real kids.</span>
          </div>
          <div className="mt-8 grid sm:grid-cols-3 gap-6">
            {SAMPLES.map((s, i) => (
              <div key={s.id} className="paper-card overflow-hidden group" data-testid={`sample-book-${i}`}>
                <div className="relative">
                  <img src={s.cover} alt={s.title} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="pill-tag absolute top-3 left-3 text-white" style={{ background: s.color }}>{s.tag}</span>
                </div>
                <div className="p-5">
                  <div className="font-display text-xl font-bold text-[#2B2D42]">{s.title}</div>
                  <div className="text-xs text-[#5C6370] mt-1">8 illustrated pages · square PDF · instant</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="mt-24">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2B2D42]">How it works.</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.id} className="paper-card p-6" data-testid={`step-${i + 1}`}>
                <div className="w-11 h-11 rounded-2xl bg-[#F2CC8F]/60 text-[#5C3F1E] flex items-center justify-center">
                  <s.icon size={20} />
                </div>
                <div className="mt-4 text-xs font-bold tracking-widest text-[#E07A5F]">STEP {i + 1}</div>
                <div className="font-display text-xl font-bold mt-1 text-[#2B2D42]">{s.title}</div>
                <div className="text-sm text-[#4A4E69] mt-2">{s.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PRICING */}
        <section className="mt-24 paper-card p-10 grid sm:grid-cols-2 gap-8 items-center bg-gradient-to-br from-[#FFFDF9] to-[#F2CC8F]/20">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2B2D42]">One flat price. One magical book.</h2>
            <p className="mt-4 text-[#4A4E69]">No subscriptions. No surprises. Preview free, pay only if you love it.</p>
            <ul className="mt-6 space-y-2 text-sm text-[#2B2D42]">
              <li>· 8 fully illustrated pages, character stays consistent</li>
              <li>· Delivered as high-quality PDF (print at home ready)</li>
              <li>· Emailed instantly + saved in your library forever</li>
            </ul>
          </div>
          <div className="text-center">
            <div className="text-6xl font-display font-black text-[#E07A5F]">$8</div>
            <div className="text-sm text-[#5C6370]">flat · USD · one book</div>
            <Link to="/create" data-testid="pricing-cta"
              className="mt-6 inline-block pill-tag bg-[#2B2D42] text-white text-base px-8 py-4 hover:bg-[#3D405B]">
              Start free preview
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-24">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2B2D42]">Common questions.</h2>
          <div className="paper-card mt-6 p-6">
            <Accordion type="single" collapsible>
              <AccordionItem value="a"><AccordionTrigger data-testid="faq-1">How long does the book take?</AccordionTrigger>
                <AccordionContent>About 60–120 seconds after checkout. You'll see a magical progress screen while we illustrate.</AccordionContent>
              </AccordionItem>
              <AccordionItem value="b"><AccordionTrigger data-testid="faq-2">Can I re-download later?</AccordionTrigger>
                <AccordionContent>Yes — click "My Library", enter your email, and we'll send you a magic-link. All your books live there forever.</AccordionContent>
              </AccordionItem>
              <AccordionItem value="c"><AccordionTrigger data-testid="faq-3">Is it safe / age appropriate?</AccordionTrigger>
                <AccordionContent>Every prompt is screened, stories are age-gated for 2–8, and images never contain scary content.</AccordionContent>
              </AccordionItem>
              <AccordionItem value="d"><AccordionTrigger data-testid="faq-4">Do you print it?</AccordionTrigger>
                <AccordionContent>Not yet — the PDF is beautifully print-at-home ready. Print-on-demand is coming soon.</AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <footer className="mt-24 pt-8 border-t border-[#e8dfd1] text-center text-sm text-[#6C757D]">
          Made with ✦ for tiny humans. © StoryForge {new Date().getFullYear()}
        </footer>
      </main>
    </div>
  );
}
